/** Pages CMS — list + create/edit + drag-drop block editor (Phase B3 full).
 *
 * - List страниц
 * - Create/edit метаданных (slug + title + seo + page_type + is_published)
 * - Блочный редактор:
 *   - drag-drop (@dnd-kit/sortable) для reorder, optimistic + bulk POST .../blocks/reorder
 *   - per-block структурированные формы (BlockDataForm) — JSON удалён
 *   - add/remove/visibility/collapse
 */

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";  // useMemo used in cross-page DnD
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { BlockDataForm } from "@/components/pages-cms/BlockDataForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface PageType {
  id: number;
  slug: string;
  name: string;
}

interface Block {
  id: number;
  block_type: string;
  data: Record<string, unknown>;
  sort_order: number;
  is_visible: boolean;
  // Phase B6: per-platform visibility + overrides.
  visible_on?: ("web" | "tma" | "mobile")[];
  platform_overrides?: Record<string, Record<string, unknown>>;
}

interface Page {
  id: number;
  slug: string;
  title: string;
  seo_title: string | null;
  seo_description: string | null;
  page_type_id: number | null;
  is_published: boolean;
  published_at: string | null;
  blocks: Block[];
}

const BLOCK_TYPES = [
  "banner_carousel",
  "features_grid",
  "category_grid",
  "product_carousel",
  "brand_strip",
  "promo_cards",
  "hero",
  "text",
  "image_strip",
  "faq",
  "html",
  "cta",
] as const;

export function PagesCmsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [expandedBlocksFor, setExpandedBlocksFor] = useState<number | null>(null);
  // Multi-page: set of pages, у которых блоки развёрнуты (можно несколько разом)
  const [expandedPages, setExpandedPages] = useState<Set<number>>(new Set());
  const [activeDragBlockId, setActiveDragBlockId] = useState<number | null>(null);

  const { data: pages, isLoading } = useQuery({
    queryKey: ["pages-cms"],
    queryFn: async () => {
      const { data } = await api.get("/pages");
      return data as Page[];
    },
  });

  // Локальное оптимистичное состояние per-page blocks (id-only)
  const [localBlocksByPage, setLocalBlocksByPage] = useState<
    Record<number, number[]>
  >({});
  useEffect(() => {
    if (!pages) return;
    const next: Record<number, number[]> = {};
    for (const p of pages) {
      next[p.id] = [...p.blocks]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((b) => b.id);
    }
    setLocalBlocksByPage(next);
  }, [pages]);

  const blockToPageMap = useMemo(() => {
    const m = new Map<number, number>();
    if (pages) {
      for (const p of pages) {
        for (const b of p.blocks) m.set(b.id, p.id);
      }
    }
    return m;
  }, [pages]);

  const findPageOfBlock = (blockId: number): number | null =>
    blockToPageMap.get(blockId) ?? null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderMutation = useMutation({
    mutationFn: async ({
      pageId,
      items,
    }: {
      pageId: number;
      items: { id: number; sort_order: number }[];
    }) => {
      await api.post(`/pages/${pageId}/blocks/reorder`, { items });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages-cms"] }),
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail ?? "Не удалось сохранить порядок");
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
    },
  });

  const moveBlockMutation = useMutation({
    mutationFn: async ({
      blockId,
      targetPageId,
      sortOrder,
    }: {
      blockId: number;
      targetPageId: number;
      sortOrder: number;
    }) => {
      await api.post(`/pages/blocks/${blockId}/move`, {
        target_page_id: targetPageId,
        sort_order: sortOrder,
      });
    },
    onSuccess: (_, vars) => {
      toast.success(`Блок перенесён в страницу #${vars.targetPageId}`);
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail ?? "Не удалось перенести");
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const id = Number(event.active.id);
    if (!Number.isNaN(id)) setActiveDragBlockId(id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Auto-expand target page при hover над свёрнутой
    const { over } = event;
    if (!over) return;
    const overId = String(over.id);
    let targetPageId: number | null = null;
    if (overId.startsWith("page-zone-")) {
      targetPageId = Number(overId.slice("page-zone-".length));
    } else {
      const n = Number(over.id);
      if (!Number.isNaN(n)) targetPageId = findPageOfBlock(n);
    }
    if (targetPageId && !expandedPages.has(targetPageId)) {
      setExpandedPages((prev) => {
        const next = new Set(prev);
        next.add(targetPageId!);
        return next;
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragBlockId(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = Number(active.id);
    if (Number.isNaN(activeId)) return;

    const sourcePageId = findPageOfBlock(activeId);
    if (!sourcePageId) return;

    // Target page: over.id может быть либо drop-zone string, либо block id
    const overStr = String(over.id);
    let targetPageId: number | null = null;
    let dropOnBlockId: number | null = null;
    if (overStr.startsWith("page-zone-")) {
      targetPageId = Number(overStr.slice("page-zone-".length));
    } else {
      const n = Number(over.id);
      if (!Number.isNaN(n)) {
        targetPageId = findPageOfBlock(n);
        dropOnBlockId = n;
      }
    }
    if (!targetPageId) return;

    // ─── Same page: reorder ──────────────────────────────────────────
    if (sourcePageId === targetPageId) {
      if (activeId === dropOnBlockId) return;
      const currentOrder = localBlocksByPage[sourcePageId] || [];
      const oldIdx = currentOrder.indexOf(activeId);
      const newIdx =
        dropOnBlockId !== null ? currentOrder.indexOf(dropOnBlockId) : currentOrder.length - 1;
      if (oldIdx === -1 || newIdx === -1) return;
      const next = arrayMove(currentOrder, oldIdx, newIdx);
      setLocalBlocksByPage((prev) => ({ ...prev, [sourcePageId]: next }));
      reorderMutation.mutate({
        pageId: sourcePageId,
        items: next.map((id, i) => ({ id, sort_order: i })),
      });
      return;
    }

    // ─── Cross-page: move block ──────────────────────────────────────
    const targetOrder = localBlocksByPage[targetPageId] || [];
    const insertAt =
      dropOnBlockId !== null ? targetOrder.indexOf(dropOnBlockId) : targetOrder.length;
    // Optimistic update local state
    setLocalBlocksByPage((prev) => ({
      ...prev,
      [sourcePageId]: (prev[sourcePageId] || []).filter((id) => id !== activeId),
      [targetPageId]: [
        ...targetOrder.slice(0, insertAt),
        activeId,
        ...targetOrder.slice(insertAt),
      ],
    }));
    moveBlockMutation.mutate({
      blockId: activeId,
      targetPageId,
      sortOrder: insertAt,
    });
  };

  const blocksMap = useMemo(() => {
    const m = new Map<number, Block>();
    if (pages) {
      for (const p of pages) {
        for (const b of p.blocks) m.set(b.id, b);
      }
    }
    return m;
  }, [pages]);

  const { data: types } = useQuery({
    queryKey: ["page-types"],
    queryFn: async () => {
      const { data } = await api.get("/page-types");
      return data as PageType[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/pages/${id}`);
    },
    onSuccess: () => {
      toast.success("Страница удалена");
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
    },
  });

  const { askDelete: askDeletePage, dialog: deletePageDialog } =
    useDeleteConfirm<number>({
      onConfirm: (id) => deleteMutation.mutateAsync(id),
      title: "Удалить страницу?",
    });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Pages CMS
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Блочный редактор. Drag-drop, per-block формы, frontend renderer.
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать страницу
        </Button>
      </div>

      {editingId === "new" && types && (
        <PageForm
          mode="create"
          types={types}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="rounded-md border border-dashed border-primary/30 bg-primary/5 p-2 text-xs text-muted-foreground">
        💡 Чтобы перенести блок между страницами — раскрой блоки обеих
        страниц, потяни за <strong>grip handle</strong>. Целевая страница
        авто-раскрывается при hover.
      </div>

      <div className="rounded-md border border-border bg-surface-2 p-3 text-xs text-muted-foreground space-y-1.5">
        <p className="font-medium text-foreground">
          🧩 Служебные slug'и — слоты для основных страниц сайта:
        </p>
        <ul className="space-y-0.5 list-disc pl-4">
          <li>
            <code className="font-mono text-primary">home</code> — главная
            (полностью заменяет хардкод). Любые блоки в любом порядке.
          </li>
          <li>
            <code className="font-mono text-primary">catalog</code> — блоки
            сверху страницы /catalog (над списком категорий).
          </li>
          <li>
            <code className="font-mono text-primary">catalog-bottom</code> —
            блоки снизу страницы /catalog.
          </li>
          <li>
            <code className="font-mono text-primary">category</code> — блоки
            сверху <em>каждой</em> страницы категории (fallback).
          </li>
          <li>
            <code className="font-mono text-primary">category-&lt;slug&gt;</code> —
            блоки только для конкретной категории, например{" "}
            <code className="font-mono">category-gostinaya</code> (приоритет
            над <code className="font-mono">category</code>).
          </li>
          <li>
            <code className="font-mono text-primary">product-extras</code> —
            блоки под карточкой <em>каждого</em> товара (fallback).
          </li>
          <li>
            <code className="font-mono text-primary">product-extras-&lt;slug&gt;</code>{" "}
            — блоки только для конкретного товара (приоритет над общим).
          </li>
        </ul>
        <p className="text-[11px] opacity-80">
          Чтобы слот заработал — страница должна быть <strong>Опубликована</strong>{" "}
          и содержать хотя бы один <em>видимый</em> блок.
        </p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
      <div className="grid grid-cols-1 gap-4">
        {pages?.length === 0 && editingId !== "new" && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Страниц нет. Создайте первую через «Создать страницу».
            </CardContent>
          </Card>
        )}
        {pages?.map((p) => (
          <Card key={p.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {p.title}
                    {p.is_published ? (
                      <Badge variant="secondary" className="text-[10px] gap-1">
                        <Eye className="w-3 h-3" /> published
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] gap-1">
                        <EyeOff className="w-3 h-3" /> draft
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    /{p.slug}
                  </div>
                </div>
                <Badge variant="outline">{p.blocks.length} блоков</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {editingId === p.id && types ? (
                <PageForm
                  mode="edit"
                  page={p}
                  types={types}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingId(p.id)}
                    >
                      Редактировать
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setExpandedPages((prev) => {
                          const next = new Set(prev);
                          if (next.has(p.id)) next.delete(p.id);
                          else next.add(p.id);
                          return next;
                        });
                        setExpandedBlocksFor(
                          expandedBlocksFor === p.id ? null : p.id,
                        );
                      }}
                    >
                      Блоки ({p.blocks.length})
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDeletePage(p.id, p.title)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}

              {(expandedPages.has(p.id) || expandedBlocksFor === p.id) && (
                <BlocksEditor
                  page={p}
                  orderedIds={localBlocksByPage[p.id] || []}
                  blocksMap={blocksMap}
                />
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <DragOverlay>
        {activeDragBlockId != null && blocksMap.get(activeDragBlockId) && (
          <div className="border rounded bg-background shadow-lg p-2 text-xs flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <Badge variant="outline" className="text-[10px]">
              {blocksMap.get(activeDragBlockId)!.block_type}
            </Badge>
            <span className="text-muted-foreground">переносим…</span>
          </div>
        )}
      </DragOverlay>
      </DndContext>
      {deletePageDialog}
    </div>
  );
}

function PageForm({
  mode,
  page,
  types,
  onClose,
}: {
  mode: "create" | "edit";
  page?: Page;
  types: PageType[];
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState(page?.slug ?? "");
  const [title, setTitle] = useState(page?.title ?? "");
  const [seoTitle, setSeoTitle] = useState(page?.seo_title ?? "");
  const [seoDescription, setSeoDescription] = useState(
    page?.seo_description ?? "",
  );
  const [pageTypeId, setPageTypeId] = useState<string>(
    page?.page_type_id ? String(page.page_type_id) : "none",
  );
  const [isPublished, setIsPublished] = useState(page?.is_published ?? false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        slug: slug.trim(),
        title: title.trim(),
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        page_type_id: pageTypeId === "none" ? null : Number(pageTypeId),
        is_published: isPublished,
      };
      if (mode === "create") {
        await api.post("/pages", payload);
      } else if (page) {
        await api.patch(`/pages/${page.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создано" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  return (
    <Card className={mode === "edit" ? "border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новая страница</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Slug *</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="about"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Заголовок *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="О компании"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Тип</Label>
          <Select value={pageTypeId} onValueChange={setPageTypeId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Без типа</SelectItem>
              {types.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>SEO title</Label>
          <Input
            value={seoTitle}
            onChange={(e) => setSeoTitle(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>SEO description</Label>
          <Textarea
            value={seoDescription}
            onChange={(e) => setSeoDescription(e.target.value)}
            rows={2}
          />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={isPublished}
            onChange={(e) =>
              setIsPublished((e.target as HTMLInputElement).checked)
            }
          />
          Опубликована
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!slug.trim() || !title.trim() || saveMutation.isPending}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Blocks editor (sortable list, без своего DndContext) ──────────────
// Cross-page drag-drop управляется родителем — здесь только SortableContext
// для блоков этой страницы + droppable zone для пустых.

function BlocksEditor({
  page,
  orderedIds,
  blocksMap,
}: {
  page: Page;
  orderedIds: number[];
  blocksMap: Map<number, Block>;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [newType, setNewType] = useState<(typeof BLOCK_TYPES)[number]>("text");

  const addMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/pages/${page.id}/blocks`, {
        block_type: newType,
        data: {},
        sort_order: orderedIds.length,
        is_visible: true,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (blockId: number) => {
      await api.delete(`/pages/blocks/${blockId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] }),
  });

  // Droppable zone — даже для пустой страницы, иначе нечем ловить drop
  const { setNodeRef: setZoneRef, isOver: zoneIsOver } = useDroppable({
    id: `page-zone-${page.id}`,
  });

  const blocks = orderedIds
    .map((id) => blocksMap.get(id))
    .filter((b): b is Block => b !== undefined);

  return (
    <div className="border-t pt-3 space-y-3">
      <div className="flex items-center gap-2">
        <Select value={newType} onValueChange={(v: any) => setNewType(v)}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BLOCK_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => addMutation.mutate()}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить блок
        </Button>
      </div>

      <SortableContext
        items={orderedIds}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setZoneRef}
          className={`min-h-[80px] rounded-md p-1 transition-colors ${
            zoneIsOver
              ? "bg-primary/10 ring-2 ring-primary/40"
              : "bg-transparent"
          }`}
        >
          {blocks.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              Блоков нет. Перетащи блок из другой страницы или добавь новый.
            </p>
          ) : (
            <div className="space-y-2">
              {blocks.map((b) => (
                <SortableBlockRow
                  key={b.id}
                  block={b}
                  pageId={page.id}
                  onDelete={() => deleteMutation.mutate(b.id)}
                />
              ))}
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

function SortableBlockRow({
  block,
  pageId,
  onDelete,
}: {
  block: Block;
  pageId: number;
  onDelete: () => void;
}): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded bg-muted/10"
    >
      <BlockRowInner
        block={block}
        pageId={pageId}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

function BlockRowInner({
  block,
  pageId,
  onDelete,
  dragHandleProps,
}: {
  block: Block;
  pageId: number;
  onDelete: () => void;
  dragHandleProps: Record<string, unknown>;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<true>({
    onConfirm: () => onDelete(),
    title: "Удалить блок?",
  });
  // Локальное состояние формы. Debounce → PATCH /blocks/{id}.
  const [localData, setLocalData] = useState<Record<string, unknown>>(
    block.data,
  );
  const [localVisible, setLocalVisible] = useState(block.is_visible);
  // Phase B6: per-platform visibility + overrides.
  const [localVisibleOn, setLocalVisibleOn] = useState<("web" | "tma" | "mobile")[]>(
    block.visible_on ?? ["web", "tma", "mobile"],
  );
  const [localOverrides, setLocalOverrides] = useState<Record<string, Record<string, unknown>>>(
    block.platform_overrides ?? {},
  );

  // Сохраняем последний серверный snapshot чтобы решить — синкать или нет.
  const serverSnapshotRef = useRef({
    data: block.data,
    is_visible: block.is_visible,
    visible_on: block.visible_on ?? ["web", "tma", "mobile"],
    platform_overrides: block.platform_overrides ?? {},
  });
  useEffect(() => {
    serverSnapshotRef.current = {
      data: block.data,
      is_visible: block.is_visible,
      visible_on: block.visible_on ?? ["web", "tma", "mobile"],
      platform_overrides: block.platform_overrides ?? {},
    };
    setLocalData(block.data);
    setLocalVisible(block.is_visible);
    setLocalVisibleOn(block.visible_on ?? ["web", "tma", "mobile"]);
    setLocalOverrides(block.platform_overrides ?? {});
  }, [block.data, block.is_visible, block.visible_on, block.platform_overrides]);

  const patchMutation = useMutation({
    mutationFn: async (payload: {
      data: Record<string, unknown>;
      is_visible: boolean;
      visible_on: ("web" | "tma" | "mobile")[];
      platform_overrides: Record<string, Record<string, unknown>>;
    }) => {
      await api.patch(`/pages/blocks/${block.id}`, {
        block_type: block.block_type,
        data: payload.data,
        sort_order: block.sort_order,
        is_visible: payload.is_visible,
        visible_on: payload.visible_on,
        platform_overrides: payload.platform_overrides,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages-cms"] });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail ?? "Не сохранилось");
    },
  });

  // Debounce 700ms — пока пользователь печатает, не флудим API.
  const dataKey = useMemo(() => JSON.stringify(localData), [localData]);
  const visibleOnKey = useMemo(() => JSON.stringify(localVisibleOn), [localVisibleOn]);
  const overridesKey = useMemo(() => JSON.stringify(localOverrides), [localOverrides]);
  useEffect(() => {
    const s = serverSnapshotRef.current;
    if (
      dataKey === JSON.stringify(s.data) &&
      localVisible === s.is_visible &&
      visibleOnKey === JSON.stringify(s.visible_on) &&
      overridesKey === JSON.stringify(s.platform_overrides)
    ) {
      return;
    }
    const t = setTimeout(() => {
      patchMutation.mutate({
        data: localData,
        is_visible: localVisible,
        visible_on: localVisibleOn,
        platform_overrides: localOverrides,
      });
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, localVisible, visibleOnKey, overridesKey]);

  const togglePlatform = (p: "web" | "tma" | "mobile") => {
    setLocalVisibleOn((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  void pageId; // referenced for potential future per-page validation hooks

  return (
    <div className="p-2 space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab touch-none p-1 text-muted-foreground hover:text-foreground"
          aria-label="Drag to reorder"
          {...dragHandleProps}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-medium hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
          <Badge variant="outline" className="text-[10px]">
            {block.block_type}
          </Badge>
        </button>
        <div className="text-xs text-muted-foreground truncate flex-1">
          {summarize(block.block_type, localData)}
        </div>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <Checkbox
            checked={localVisible}
            onChange={(e) =>
              setLocalVisible((e.target as HTMLInputElement).checked)
            }
          />
          visible
        </label>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => askDelete(true, block.block_type)}
        >
          <Trash2 className="w-3.5 h-3.5 text-destructive" />
        </Button>
      </div>

      {expanded && (
        <div className="pt-2 pl-7 border-t mt-2 space-y-3">
          <BlockDataForm
            blockType={block.block_type}
            data={localData}
            onChange={setLocalData}
          />

          {/* Phase B6: показывать на платформах */}
          <div className="rounded-md border border-border bg-surface-2 p-2 space-y-1.5">
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Показывать на
            </div>
            <div className="flex flex-wrap gap-2">
              {(["web", "tma", "mobile"] as const).map((p) => (
                <label
                  key={p}
                  className="flex items-center gap-1.5 text-xs cursor-pointer"
                >
                  <Checkbox
                    checked={localVisibleOn.includes(p)}
                    onChange={() => togglePlatform(p)}
                  />
                  {p}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Если platform не отмечена — блок не попадёт в `/api/v1/pages/$slug?platform=$p`.
            </p>
          </div>

          {/* Phase B6: per-platform overrides */}
          <details className="rounded-md border border-border bg-surface-2 p-2 text-xs">
            <summary className="cursor-pointer font-medium text-foreground">
              Per-platform overrides
              {Object.keys(localOverrides).length > 0 && (
                <span className="ml-1 text-muted-foreground">
                  ({Object.keys(localOverrides).filter((k) => Object.keys(localOverrides[k] ?? {}).length > 0).join(", ") || "пусто"})
                </span>
              )}
            </summary>
            <div className="mt-2 space-y-2">
              <p className="text-[10px] text-muted-foreground">
                JSON для каждой платформы. Поля мерджатся поверх `data`.
                Пример: <code>{`{"title": "..."}`}</code>. Пустые ключи
                игнорируются.
              </p>
              {(["mobile", "tma", "web"] as const).map((p) => (
                <PlatformOverrideField
                  key={p}
                  platform={p}
                  value={localOverrides[p] ?? {}}
                  onChange={(next) =>
                    setLocalOverrides((prev) => {
                      const out = { ...prev };
                      if (next == null || Object.keys(next).length === 0) {
                        delete out[p];
                      } else {
                        out[p] = next;
                      }
                      return out;
                    })
                  }
                />
              ))}
            </div>
          </details>
        </div>
      )}
      {deleteDialog}
    </div>
  );
}

function PlatformOverrideField({
  platform,
  value,
  onChange,
}: {
  platform: "web" | "tma" | "mobile";
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}): JSX.Element {
  const [text, setText] = useState(() =>
    Object.keys(value).length ? JSON.stringify(value, null, 2) : "",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Sync incoming server snapshot, но только когда раздел не редактируется.
    const incoming = Object.keys(value).length
      ? JSON.stringify(value, null, 2)
      : "";
    setText((prev) => (prev === incoming ? prev : incoming));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const apply = (raw: string) => {
    setText(raw);
    if (!raw.trim()) {
      setError(null);
      onChange({});
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed == null || Array.isArray(parsed)) {
        setError("Должен быть JSON-объект");
        return;
      }
      setError(null);
      onChange(parsed as Record<string, unknown>);
    } catch (e) {
      setError("Невалидный JSON");
    }
  };

  return (
    <div>
      <div className="text-[10px] font-mono text-muted-foreground mb-0.5">
        {platform}
      </div>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => apply(e.target.value)}
        placeholder={`{"title": "Hello ${platform}"}`}
        className="w-full font-mono text-[11px] rounded-md border border-border bg-background px-2 py-1 outline-none focus:border-primary"
      />
      {error && (
        <div className="text-[10px] text-destructive mt-0.5">{error}</div>
      )}
    </div>
  );
}

function summarize(type: string, data: Record<string, unknown>): string {
  const s = (k: string) => (data[k] as string) ?? "";
  switch (type) {
    case "hero":
    case "text":
    case "cta":
    case "faq":
    case "image_strip":
    case "category_grid":
    case "product_carousel":
      return s("title") || s("heading") || "—";
    case "html":
      return s("html").slice(0, 80);
    default:
      return "";
  }
}
